import { Loader2 } from 'lucide-react';
import { useId } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface WizardFooterProps {
  consentChecked: boolean;
  onConsentChange: (checked: boolean) => void;
  onSaveDraft: () => void;
  onCancel: () => void;
  onNext: () => void;
  isSaving?: boolean;
  nextDisabled?: boolean;
}

export function WizardFooter({
  consentChecked,
  onConsentChange,
  onSaveDraft,
  onCancel,
  onNext,
  isSaving = false,
  nextDisabled = false,
}: WizardFooterProps) {
  const consentId = useId();

  return (
    <footer className="fixed bottom-0 left-0 right-0 z-10 border-t border-[#D4DAE0] bg-white px-4 py-4 md:px-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {/* Consent checkbox */}
        <div className="flex items-start gap-2 md:items-center">
          <Checkbox
            id={consentId}
            checked={consentChecked}
            onCheckedChange={(checked) => onConsentChange(checked === true)}
            className="mt-0.5 md:mt-0"
          />
          <Label htmlFor={consentId} className="text-[14px] leading-tight text-[#051923]">
            I have consent from the aforementioned client to perform this risk assessment.
          </Label>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:gap-4">
          <button
            type="button"
            onClick={onSaveDraft}
            disabled={isSaving}
            className="text-center text-[14px] text-[#051923] underline hover:no-underline disabled:opacity-50 sm:text-left"
          >
            Save Draft
          </button>
          <div className="flex gap-3 sm:gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
              className="h-[45px] flex-1 rounded-[5px] border-[#131313] px-6 text-[16px] font-medium text-[#131313] hover:bg-gray-50 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onNext}
              disabled={isSaving || nextDisabled || !consentChecked}
              className="h-[45px] flex-1 rounded-[5px] bg-[#131313] px-6 text-[16px] font-medium text-white hover:bg-[#131313]/90 sm:flex-none"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Next'
              )}
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
}
